import { TooltipContent } from "@/components/ui/tooltip";
import { Lock, TrendingUp, ArrowUp, Star, Calendar, AlertTriangle, BarChart2 } from "lucide-react";
import { buttonVariants } from "./ui/button";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface CalendarTooltipProps {
  value: number;
  count: number;
  totalScore?: number;
  dailySum?: number;
  isPremiumFeature?: boolean;
  volatility?: 'low' | 'medium' | 'high';
}

/**
 * Mini-chart component to visualize event impact distribution
 */
const MiniBarChart = ({ count, value }: { count: number; value: number }) => {
  // Generate mock data for the chart
  const generateBars = () => {
    const bars = [];
    const maxBars = 7;
    const actualBars = Math.min(count, maxBars);
    
    for (let i = 0; i < actualBars; i++) {
      // Generate random heights but ensure they align with overall volatility
      const multiplier = i === 0 ? 0.9 : Math.random() * 0.8 + 0.2;
      const height = `${Math.max(15, Math.min(100, Math.round(multiplier * 100)))}%`;
      
      bars.push(
        <div 
          key={i}
          className="w-1 sm:w-1.5 bg-primary/70 rounded-t-sm"
          style={{ height }}
        />
      );
    }
    
    // Add placeholder bars if less than maxBars
    for (let i = actualBars; i < maxBars; i++) {
      bars.push(
        <div 
          key={i + actualBars}
          className="w-1 sm:w-1.5 bg-muted/30 rounded-t-sm"
          style={{ height: '15%' }}
        />
      );
    }
    
    return bars;
  };
  
  return (
    <div className="mt-2 mb-1">
      <div className="flex items-center gap-2 mb-1">
        <BarChart2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs">Event Impact Distribution</span>
      </div>
      <div className="flex h-8 sm:h-10 items-end justify-between gap-0.5 rounded-sm border border-border p-0.5 sm:p-1 bg-background/50">
        {generateBars()}
      </div>
    </div>
  );
};

export const CalendarTooltip = ({ 
  value, 
  count, 
  totalScore, 
  dailySum,
  isPremiumFeature = false,
  volatility: propVolatility
}: CalendarTooltipProps) => {
  if (isPremiumFeature) {
    return (
      <TooltipContent 
        side="top" 
        className="bg-background/95 border border-border p-4 min-w-[280px] max-w-[320px]"
        sideOffset={5}
        sticky="always"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Premium Feature</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Subscribe to our premium plan to access historical and future economic events beyond the 2-day window.
          </p>
          <Link 
            to="/profile" 
            className={cn(
              buttonVariants({ variant: "default", size: "sm" }),
              "w-full"
            )}
          >
            Upgrade Now
          </Link>
        </div>
      </TooltipContent>
    );
  }

  // Get volatility level based on value if not provided as prop
  const getVolatilityLevel = () => {
    if (propVolatility) return propVolatility;
    
    if (value >= 5) return 'high';
    if (value >= 2.5) return 'medium';
    return 'low';
  };
  
  const volatility = getVolatilityLevel();

  const getVolatilityIcon = () => {
    switch (volatility) {
      case 'high':
        return <TrendingUp className="h-5 w-5 text-purple-400" />;
      case 'medium':
        return <ArrowUp className="h-5 w-5 text-blue-400" />;
      case 'low':
        return <Star className="h-5 w-5 text-cyan-400" />;
      default:
        return null;
    }
  };
  
  const getVolatilityText = () => {
    switch (volatility) {
      case 'high':
        return "High volatility expected";
      case 'medium':
        return "Medium volatility expected";
      case 'low':
        return "Low volatility expected";
      default:
        return "No data available";
    }
  };

  const getVolatilityMeter = () => {
    return (
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={cn(
            "h-full rounded-full",
            {
              'bg-purple-500': volatility === 'high',
              'bg-blue-500': volatility === 'medium',
              'bg-cyan-500': volatility === 'low',
            }
          )}
          style={{ 
            width: volatility === 'high' 
              ? '100%' 
              : volatility === 'medium' 
                ? '66%' 
                : '33%' 
          }}
        />
      </div>
    );
  };

  return (
    <TooltipContent 
      side="top" 
      className="bg-background/95 border border-border p-4 min-w-[280px] max-w-[320px]"
      sideOffset={5}
      sticky="always"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          {getVolatilityIcon()}
          <div>
            <h3 className="text-lg font-semibold mb-1">Economic Impact</h3>
            <p className="text-sm text-muted-foreground">
              {getVolatilityText()}
            </p>
          </div>
        </div>
        
        {getVolatilityMeter()}
        
        {count > 0 && <MiniBarChart count={count} value={value} />}
        
        <div className="space-y-2 pt-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <span>Volatility Score</span>
            </div>
            <span className="font-mono font-medium">{value?.toFixed(2) || '0'}</span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Number of Events</span>
            </div>
            <span>{count} event{count !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Volatility is calculated using weighted sums of adjacent days (±2 days).
          </p>
        </div>
      </div>
    </TooltipContent>
  );
};